import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Container, Modal, ModalBody, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { RefreshCw, X } from "react-feather";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { getDraftOrders } from "../../utils/apiCalls";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";

const STATUS_COLOR = {
  pending_payment: { bg: C.orangeLight, fg: C.orange },
  order_created: { bg: C.greenLight, fg: C.green },
  expired: { bg: C.coralLight, fg: C.coral },
};

const Pill = ({ value, palette }) => {
  const c = palette[value] || { bg: C.bg, fg: C.textMed };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.fg }} />
      {(value || "").replace(/_/g, " ").toUpperCase()}
    </span>
  );
};

const filterSelectStyle = {
  padding: "7px 12px",
  fontSize: 12,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  fontFamily: "inherit",
  outline: "none",
  color: C.text,
  background: C.white,
  cursor: "pointer",
  minWidth: 140,
};

const DraftOrders = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector(
    (state) => state.merchant || {}
  );
  const selectedMerchant =
    userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [isLoading, setIsLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [detail, setDetail] = useState(null);

  const [filters, setFilters] = useState({
    MerchantId: selectedMerchant || "",
    status: "",
    search: "",
    limit: 20,
  });

  const fetchDrafts = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      const data = await getDraftOrders(page, filters.limit, filters);
      if (data) {
        setDrafts(data.rows || []);
        setTotalPages(data.totalPages || 1);
      } else {
        setDrafts([]);
        setTotalPages(1);
      }
      setIsLoading(false);
    },
    [filters]
  );

  useEffect(() => {
    fetchDrafts(currentPage);
  }, [fetchDrafts, currentPage]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, MerchantId: selectedMerchant || "" }));
    setCurrentPage(1);
  }, [selectedMerchant]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const customerName = (user) =>
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.phoneNo ||
    user?.email ||
    "—";

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).slice(
    Math.max(0, currentPage - 3),
    Math.min(totalPages, currentPage + 2)
  );

  return (
    <Fragment>
      <Breadcrumb title="Draft Orders" parent="Orders" />
      <Container fluid={true}>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <div
                onClick={() => fetchDrafts(currentPage)}
                title="Refresh"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: C.white,
                  flexShrink: 0,
                }}
              >
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <input
                placeholder="Search external id"
                value={filters.search}
                onChange={(e) => {
                  setFilters({ ...filters, search: e.target.value });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setCurrentPage(1);
                    fetchDrafts(1);
                  }
                }}
                style={{ ...filterSelectStyle, minWidth: 240, cursor: "text" }}
              />

              <select
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                  setCurrentPage(1);
                }}
                style={filterSelectStyle}
              >
                <option value="">All statuses</option>
                <option value="pending_payment">Pending payment</option>
                <option value="order_created">Order created</option>
                <option value="expired">Expired</option>
              </select>

              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 12,
                  color: C.textLight,
                  background: C.bg,
                  padding: "4px 12px",
                  borderRadius: 20,
                }}
              >
                {drafts?.length || 0} drafts
              </span>
            </div>

            {isLoading ? (
              <RLoader />
            ) : (
              <div
                style={{
                  overflowX: "auto",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                }}
              >
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>ID</th>
                      <th style={tbl.th}>Created</th>
                      <th style={tbl.th}>External ID</th>
                      <th style={tbl.th}>Amount</th>
                      <th style={tbl.th}>Customer</th>
                      <th style={tbl.th}>Branch</th>
                      <th style={tbl.th}>Order</th>
                      <th style={tbl.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drafts?.length > 0 ? (
                      drafts.map((d) => (
                        <tr
                          key={d.id}
                          className="rpt-row"
                          style={{ cursor: "pointer" }}
                          onClick={() => setDetail(d)}
                        >
                          <td style={{ ...tbl.td, color: C.coral, fontWeight: 700 }}>#{d.id}</td>
                          <td style={tbl.td}>{formatDate(d.createdAt)}</td>
                          <td style={{ ...tbl.td, fontWeight: 600 }}>{d.externalId}</td>
                          <td style={tbl.td}>{d.amount ?? "—"}</td>
                          <td style={tbl.td}>{customerName(d.User)}</td>
                          <td style={tbl.td}>{d.Branch?.name || "—"}</td>
                          <td style={tbl.td}>{d.orderId ? `#${d.orderId}` : "—"}</td>
                          <td style={tbl.td}>
                            <Pill value={d.status} palette={STATUS_COLOR} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="8"
                          style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}
                        >
                          No draft orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {drafts?.length > 0 && totalPages > 1 && (
              <Pagination className="d-flex justify-content-end mt-3 mb-0">
                <PaginationItem disabled={currentPage <= 1}>
                  <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                </PaginationItem>
                {pageNumbers.map((i) => (
                  <PaginationItem active={i === currentPage} key={i}>
                    <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={currentPage >= totalPages}>
                  <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                </PaginationItem>
              </Pagination>
            )}
          </RCard>
        </div>

        <DraftDetailModal draft={detail} onClose={() => setDetail(null)} customerName={customerName} />
      </Container>
    </Fragment>
  );
};

const DetailRow = ({ label, value, mono }) => (
  <div style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: `1px solid ${C.borderLight}` }}>
    <div style={{ flex: "0 0 150px", fontSize: 12, fontWeight: 600, color: C.textMed }}>{label}</div>
    <div
      style={{
        flex: 1,
        fontSize: 13,
        color: C.text,
        wordBreak: "break-word",
        fontFamily: mono ? "monospace" : "inherit",
      }}
    >
      {value === null || value === undefined || value === "" ? "—" : value}
    </div>
  </div>
);

const JsonBlock = ({ data }) => (
  <pre
    style={{
      margin: 0,
      padding: 12,
      background: C.bg,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      fontSize: 11,
      lineHeight: 1.5,
      color: C.text,
      maxHeight: 240,
      overflow: "auto",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}
  >
    {(() => {
      try {
        return JSON.stringify(typeof data === "string" ? JSON.parse(data) : data, null, 2);
      } catch (e) {
        return String(data);
      }
    })()}
  </pre>
);

const SectionTitle = ({ title }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: C.coral,
      marginTop: 18,
      marginBottom: 4,
    }}
  >
    {title}
  </div>
);

const DraftDetailModal = ({ draft, onClose, customerName }) => {
  if (!draft) return null;
  return (
    <Modal isOpen={!!draft} toggle={onClose} centered size="lg">
      <style>{reportingGlobalCSS}</style>
      <ModalBody style={{ padding: 0 }} className="rpt-wrap">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Draft #{draft.id}</span>
            <Pill value={draft.status} palette={STATUS_COLOR} />
          </div>
          <div onClick={onClose} style={{ cursor: "pointer", color: C.textMed, display: "flex" }} title="Close">
            <X size={20} />
          </div>
        </div>

        <div style={{ padding: "12px 22px 22px" }}>
          <SectionTitle title="Draft" />
          <DetailRow label="External ID" value={draft.externalId} mono />
          <DetailRow label="Amount" value={draft.amount} />
          <DetailRow label="Created" value={formatDate(draft.createdAt)} />
          <DetailRow label="Updated" value={formatDate(draft.updatedAt)} />

          <SectionTitle title="Order" />
          <DetailRow
            label="Order ID"
            value={draft.orderId ? <span style={{ fontWeight: 700, color: C.green }}>#{draft.orderId}</span> : null}
          />

          <SectionTitle title="Customer" />
          <DetailRow label="Customer" value={customerName(draft.User)} />
          <DetailRow label="Phone" value={draft.User?.phoneNo} />
          <DetailRow label="Branch" value={draft.Branch?.name} />

          {draft.orderPayload ? (
            <>
              <SectionTitle title="Stored order payload" />
              <JsonBlock data={draft.orderPayload} />
            </>
          ) : null}
        </div>
      </ModalBody>
    </Modal>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return (
    date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    ", " +
    date.toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric", hour12: true })
  );
};

export default DraftOrders;
